const HELD_KEY = "ahcp.checkout.heldOrder";
const AMOUNT_KEY = "ahcp.checkout.capturedAmount";
const IN_FLIGHT_KEY = "ahcp.checkout.paymentInFlight";
const FINGERPRINT_KEY = "ahcp.checkout.cartFingerprint";

function readJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(storage: Storage, key: string, value: unknown): void {
  storage.setItem(key, JSON.stringify(value));
}

export interface HeldCheckoutOrder {
  orderId: string;
  orderNumber?: string;
  invoiceId?: string;
  paymentMethod?: number;
  invoiceFailed?: boolean;
  /** Cart id:qty fingerprint used to reuse this unpaid order. */
  fingerprint?: string;
}

export function cartFingerprint(items: { product: { id: string }; quantity: number }[]): string {
  return items
    .map((line) => `${line.product.id}:${line.quantity}`)
    .sort()
    .join("|");
}

export function loadHeldCheckoutOrder(): HeldCheckoutOrder | null {
  const fromSession = readJson<HeldCheckoutOrder>(sessionStorage, HELD_KEY);
  const fromLocal = readJson<HeldCheckoutOrder>(localStorage, HELD_KEY);
  const parsed = fromSession?.orderId ? fromSession : fromLocal;
  if (!parsed?.orderId) return null;
  return parsed;
}

export function saveHeldCheckoutOrder(held: HeldCheckoutOrder): void {
  writeJson(sessionStorage, HELD_KEY, held);
  writeJson(localStorage, HELD_KEY, held);
}

export function clearHeldCheckoutOrder(): void {
  sessionStorage.removeItem(HELD_KEY);
  sessionStorage.removeItem(FINGERPRINT_KEY);
  localStorage.removeItem(HELD_KEY);
  localStorage.removeItem(FINGERPRINT_KEY);
}

export function isUnpaidHeldCheckoutOrder(orderId: string | number | null | undefined): boolean {
  const heldId = loadHeldCheckoutOrder()?.orderId;
  if (!heldId) return false;
  return String(orderId ?? "").trim() === String(heldId).trim();
}

export function saveCheckoutFingerprint(fp: string): void {
  sessionStorage.setItem(FINGERPRINT_KEY, fp);
  localStorage.setItem(FINGERPRINT_KEY, fp);
}

export function loadCheckoutFingerprint(): string {
  return sessionStorage.getItem(FINGERPRINT_KEY) ?? localStorage.getItem(FINGERPRINT_KEY) ?? "";
}

export function saveCapturedAmount(amount: number): void {
  sessionStorage.setItem(AMOUNT_KEY, String(amount));
}

export function loadCapturedAmount(): number {
  const n = Number(sessionStorage.getItem(AMOUNT_KEY));
  return Number.isFinite(n) ? n : 0;
}

export function setPaymentInFlight(on: boolean): void {
  if (on) sessionStorage.setItem(IN_FLIGHT_KEY, String(Date.now()));
  else sessionStorage.removeItem(IN_FLIGHT_KEY);
}

export function isPaymentInFlight(): boolean {
  const raw = sessionStorage.getItem(IN_FLIGHT_KEY);
  if (!raw) return false;
  const started = Number(raw);
  if (!Number.isFinite(started)) return false;
  return Date.now() - started < 15 * 60 * 1000;
}

export const OrderPaymentMethod = {
  CreditCard: 0,
  ZainCash: 1,
  CashOnDelivery: 2,
  QiCard: 3,
} as const;
